import { Button, Callout, Icon, tCount, type I18n } from '@signbento/ui'
import type { ReactElement } from 'react'
import {
  ACCOUNT_DELETION_RETENTION_KEYS,
  retentionWindowKey,
  type StoredDeletionReceipt,
} from '../lib/account-lifecycle.js'
import { signInRequired } from '../lib/device-accounts.js'
import { downloadText } from '../lib/download.js'
import { formatLocalDate } from '../lib/envelope-status.js'

/** "7 years" rather than "2555 days": whole years read as years. */
export function retentionSpan(i18n: I18n, days: number): string {
  return days >= 365 && days % 365 === 0
    ? tCount(i18n, 'years', days / 365)
    : tCount(i18n, 'days', days)
}

/**
 * What an account deletion did, as its receipt records it (§10.6). Settings shows it after the
 * deletion; behind the sign-in gate the gate shows it, because the deleted account's vault closes
 * at once (sign-in-once spec §1). The page says it in sentences with local dates; the service's full
 * record, counters included, is the receipt file a person can download and give to support.
 */
export function DeletionReceiptView({
  i18n,
  receipt,
}: {
  readonly i18n: I18n
  readonly receipt: StoredDeletionReceipt
}): ReactElement {
  const record = receipt.receipt
  return (
    <Callout tone="success" role="status">
      <p data-testid="account-delete-receipt">
        {i18n.t('settings.account_delete_receipt', { receipt: record.receipt_id })}
      </p>
      <p data-testid="account-delete-receipt-at">
        {i18n.t('settings.account_delete_receipt_at', {
          at: formatLocalDate(record.deleted_at, i18n.locale),
        })}
      </p>
      {record.idempotent_replay ? (
        <p data-testid="account-delete-replay">{i18n.t('settings.account_delete_replay')}</p>
      ) : null}
      <p data-testid="account-delete-disposition">
        {i18n.t('settings.account_delete_disposition', {
          requests: record.disposition.active_requests_voided,
        })}
      </p>
      <ul data-testid="account-delete-retention-receipt">
        {record.retention.map((entry) => {
          const windowKey = retentionWindowKey(entry)
          return (
            <li key={entry.retention_class}>
              {i18n.t(ACCOUNT_DELETION_RETENTION_KEYS[entry.retention_class])}
              {windowKey === null
                ? null
                : ` ${i18n.t(windowKey, { days: retentionSpan(i18n, entry.window_days ?? 0) })}`}
            </li>
          )
        })}
      </ul>
      <p data-testid="account-delete-erasure-receipt">
        {i18n.t('settings.account_delete_outside_erasure')}
      </p>
      {/* Behind the gate the deleted account's documents close with it; a backup is the way back. */}
      {signInRequired() ? (
        <p data-testid="account-delete-restore-hint">
          {i18n.t('settings.account_delete_restore_hint')}
        </p>
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<Icon name="download" />}
        data-testid="account-delete-receipt-download"
        onClick={() =>
          downloadText(
            `deletion-receipt-${record.receipt_id}.json`,
            JSON.stringify(record, null, 2),
          )
        }
      >
        {i18n.t('settings.account_delete_receipt_download')}
      </Button>
    </Callout>
  )
}
